<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useWindowScroll, onClickOutside, useParentElement, useElementBounding } from '@vueuse/core';
import type { UiDropdownProps } from './types';

const emit = defineEmits<{ close: [] }>();

const {
  visible,
  offset = 8,
  width = 300,
  position = 'right',
  styled = true,
  fullWidth = false,
  maxHeight,
} = defineProps<UiDropdownProps>();

const { y: windowY } = useWindowScroll();

const popup = ref<HTMLElement>();

const parentEl = useParentElement();

const { width: parentElWidth } = useElementBounding(parentEl);
const { width: currentElWidth } = useElementBounding(popup);

const elStyle = computed(() => ({
  width: fullWidth ? `${parentElWidth.value}px` : `${width}px`,
  transform: `translate(${position === 'right'
    ? parentElWidth.value - currentElWidth.value
    : 0
  }px, ${offset}px)`,
  maxHeight: maxHeight ? `${maxHeight}px` : 'unset',
  overflow: 'auto',
}));

const onClose = () => emit('close');

onClickOutside(popup, onClose);

watch(windowY, (y) => {
  if (y > 50) onClose();
});
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-in-out"
    enter-from-class="opacity-0"
    leave-active-class="transition-all duration-300 ease-in-out"
    leave-to-class="opacity-0"
  >
    <div
      v-if="visible"
      ref="popup"
      :style="elStyle"
      :class="[
        'ui-dropdown',
        styled && 'flex flex-col rounded-lg bg-white border border-gray-50 shadow-md',
      ]"
    >
      <div
        v-if="$slots.header"
        class="border-b border-gray-100"
      >
        <slot name="header" />
      </div>
      <slot />
    </div>
  </Transition>
</template>

<style scoped>
.ui-dropdown {
  position: absolute;
  z-index: 10;
}

.ui-dropdown::-webkit-scrollbar {
  width: 3px;
}

.ui-dropdown::-webkit-scrollbar-thumb {
  background-color: #DCDCDE;
  border-radius: 25px;
}
</style>
